<script lang="ts" setup>
import IssueBadge from './IssueBadge.vue'
import type { Appointment, IssueBadgeInfo } from '~/models/appointments'
import type { useReturnRulesLatest } from '~/stores/returnRulesLatest'
import type {
  ContainerType,
  ShippingLine,
  TerminalName,
} from '~/services/apiClient'
import { ReceivingRule } from '~/services/apiClient'
import type { AppointmentTransaction } from '~/models/groupedAppointments'

const props = defineProps<{
  badges: IssueBadgeInfo[]
  appointmentTransaction: AppointmentTransaction
  returnRulesStore: ReturnType<typeof useReturnRulesLatest>
}>()
const hasHoldsBadge = (): IssueBadgeInfo | undefined => {
  if (
    props.appointmentTransaction?.primary_appointment.appointment.is_import_out
  ) {
    const parsedHolds =
      props.appointmentTransaction?.primary_appointment.import_status
        ?.parsed_holds
    if (parsedHolds && parsedHolds.length > 0) {
      const holdsDescription = parsedHolds.map((hold) => hold.type).join(', ')
      return {
        message: 'Has holds',
        type: 'warning',
        tooltip: `Container has holds that may prevent pickup: ${holdsDescription}`,
        issueType: 'hasHolds',
      }
    }
    return undefined
  }
}

const notAvailableForPickupBadge = (): IssueBadgeInfo | undefined => {
  if (
    !props.appointmentTransaction?.primary_appointment.appointment.is_import_out
  ) {
    return undefined
  }
  if (
    props.appointmentTransaction?.primary_appointment.import_status
      ?.available_for_pickup === false
  ) {
    return {
      message: 'Not ready for pickup',
      type: 'warning',
      tooltip: 'Container is not available for pickup',
      issueType: 'notAvailableForPickup',
    }
  }
  return undefined
}

const missingLicensePlateBadge = (): IssueBadgeInfo | undefined => {
  if (
    props.appointmentTransaction?.primary_appointment.appointment
      .missing_required_license_plate
  ) {
    return {
      message: 'Missing license plate',
      type: 'warning',
      tooltip: 'License plate is required for this terminal',
      issueType: 'missingLicensePlate',
    }
  }
  return undefined
}

const ruleBadge = (
  shippingLine: ShippingLine | undefined,
  containerType: ContainerType | undefined,
  terminal: TerminalName | undefined,
  appointment: Appointment
): IssueBadgeInfo | undefined => {
  if (!appointment || !shippingLine || !containerType || !terminal) return

  const rule = props.returnRulesStore.lookupRule(
    shippingLine,
    containerType,
    terminal,
    appointment.window_start,
    appointment.shift
  )

  if (!rule) return

  const isDual = appointment.linked_appointments?.length > 0
  const isSingle = !isDual

  const violates =
    (isSingle && rule.rule !== ReceivingRule.Open) ||
    (isDual && rule.rule === ReceivingRule.Closed)

  if (violates) {
    return {
      message: 'Violates Return Rules',
      type: 'danger',
      tooltip: `Return rule for ${shippingLine}/${containerType} at ${terminal} is "${rule.rule}"`,
      issueType: 'violatesReturnRules',
    }
  }
}

// Compute all badges once, without expensive width calculation
const allBadges = computed(() => {
  const base: IssueBadgeInfo[] = []
  const holdsBadge = hasHoldsBadge()
  if (holdsBadge) base.push(holdsBadge)
  const pickupBadge = notAvailableForPickupBadge()
  if (pickupBadge) base.push(pickupBadge)

  if (
    props.appointmentTransaction?.inbound_appointment?.appointment.is_empty_in
  ) {
    const rulesBadge = ruleBadge(
      props.appointmentTransaction?.inbound_appointment?.watch.shipping_line ||
        undefined,
      props.appointmentTransaction?.inbound_appointment?.watch.container_type ||
        undefined,
      props.appointmentTransaction?.inbound_appointment?.appointment.terminal,
      props.appointmentTransaction?.inbound_appointment?.appointment
    )
    if (rulesBadge) base.push(rulesBadge)
  }

  const licensePlateBadge = missingLicensePlateBadge()
  if (licensePlateBadge) base.push(licensePlateBadge)
  base.push(...props.badges)
  return base
})
</script>

<template>
  <div class="badge-holder w-full">
    <IssueBadge
      v-for="badge in allBadges"
      :key="badge.message"
      :badge="badge"
    />
  </div>
</template>

<style lang="scss" scoped>
.badge-holder {
  min-height: 24px;
}
</style>
